<template>
  <div ref='modal-wrapper' v-if='showModal'>
    <component
      :is='templateKey'
      :agreement='agreement'
      :modalCurrentUiState='modalCurrentUiState'
      :verticalIds='verticalIds'
      :selectedVerticalIds='selectedVerticalIds'
      :buttonTextCancel='buttonTextCancel'
      :buttonTextContinue='buttonTextContinue'
      @submit-agreement='handleSubmitAgreementEvent'
      @cancel-offers='handleCancelOffersEvent'
      @update-phone='handleUpdatePhoneEvent'></component>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Modal from '@/components/Shared/Modal.vue';
import AccountClient from '@/utilities/account-client';
import OffersClient from '@/utilities/offers-client';
import IpClient from '@/utilities/ip-client';
import TrustedForm from '@/utilities/trusted-form';
import OffersModalTemplates from '@/components/OffersModalTemplates';

export const defaultTemplateKey = 'aphrodite';

export const ModalStates = {
  Review: 'review',
  Inquiry: 'inquiry',
  Complete: 'complete',
};

export const ModalActiveUiStates = [ModalStates.Review, ModalStates.Inquiry];

export default {
  components: {
    Modal,
    ...OffersModalTemplates,
  },
  computed: {
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    ...mapState('User', {
      user: state => state.currentUser,
    }),
    ...mapGetters('User', ['userAuthToken']),
    showModal() {
      if (!this.apisDidLoad) {
        return false;
      }
      return (
        !this.apisDidError
        && this.isDueForDisplay
        && this.hasAgreement
        && ModalActiveUiStates.includes(this.modalCurrentUiState)
      );
    },
    buttonTextCancel() {
      if (this.agreement && this.agreement.buttonTextCancel) {
        return this.agreement.buttonTextCancel;
      }
      return 'Cancel';
    },
    buttonTextContinue() {
      if (this.agreement && this.agreement.buttonTextContinue) {
        return this.agreement.buttonTextContinue;
      }
      return 'Continue';
    },
    selectedVerticalIds() {
      return this.aggregateSelectedVerticalIds(this.verticalIds);
    },
    agreementId() {
      if (this.agreement && this.agreement.agreementId) {
        return this.agreement.agreementId;
      }
      return null;
    },
  },
  data() {
    return {
      agreement: null,
      apisAreLoading: false,
      apisDidError: false,
      apisDidLoad: false,
      apisError: null,
      clients: {},
      company: {},
      companyUuid: null,
      hasAgreement: false,
      hasPhoneNumber: false,
      isDueForDisplay: false,
      modalCurrentUiState: ModalStates.Review,
      templateKey: null,
      trustedForm: null,
      userIp: null,
      verticalIds: {},
    };
  },
  methods: {
    async handleCancelOffersEvent() {
      this.clearSelectedVerticalIds();
      await this.handleSubmitAgreement();
      this.modalCurrentUiState = ModalStates.Complete;
    },
    async handleSubmitAgreementEvent() {
      await this.handleSubmitAgreement();
      this.maybeInquirePhoneNumber();
    },
    handleUpdatePhoneEvent({ phoneType, phoneNumber }) {
      this.updatePhoneNumber(phoneType, phoneNumber);
    },
    initializeClients() {
      this.clients.accountClient = new AccountClient(
        process.env.VUE_APP_ROOT_API,
        this.userAuthToken,
      );
      this.clients.offersClient = new OffersClient(this.userAuthToken);
      this.clients.ipClient = new IpClient('https://api.ipify.org/');
    },
    async initializeAllApis() {
      if (!this.isRouteParamAvailable('folderId')) {
        return null;
      }
      if (this.apisDidLoad || this.apisAreLoading) {
        return null;
      }
      this.apisAreLoading = true;
      let userApisResponse;
      let offersApisResponse;
      try {
        userApisResponse = await this.initializeUserApis();
        offersApisResponse = await this.initializeOffersApis();
        this.apisDidLoad = true;
        this.apisDidError = false;
      } catch (error) {
        this.apisError = error;
        this.apisDidError = true;
      } finally {
        this.apisAreLoading = false;
      }
      return {
        userApisResponse,
        offersApisResponse,
      };
    },
    async initializeUserApis() {
      const uuid = await this.requestCompanyUuid();
      const company = await this.requestCompany(uuid);
      const hasPhoneNumber = await this.requestHasPhoneNumber();
      const userIp = await this.requestUserIp();
      return {
        uuid,
        company,
        hasPhoneNumber,
        userIp,
      };
    },
    async initializeOffersApis() {
      const offersApis = await Promise.all([
        this.requestIsDueForDisplay(),
        this.requestAgreement(),
      ]);
      return { offersApis };
    },
    initializeVerticals() {
      this.verticalIds = this.agreement.offers.reduce(
        (verticalIds, offer) => ({
          ...verticalIds,
          [offer.verticalIds]: false,
        }),
        {},
      );
      return this.verticalIds;
    },
    isRouteParamAvailable(param) {
      return this.$route && this.$route.params && this.$route.params[param];
    },
    async requestIsDueForDisplay() {
      const isDueForDisplay = await this.clients.offersClient.isDueForDisplay('inspection');
      this.isDueForDisplay = isDueForDisplay;
      return this.isDueForDisplay;
    },
    async requestAgreement() {
      const { postalCode, state, country } = this.$store.state.Inspection.inspections[0];
      if (!country || (country !== 'US' && country !== 'Un')) {
        return {};
      }
      const requestAgreementParams = {
        location: 'inspection',
        companyId: this.companyId,
        inspectionId: this.$route.params.folderId,
        includeTemplateKey: true,
        postalCode,
        state,
      };
      const agreements = await this.clients.offersClient.getAgreements(requestAgreementParams);

      // Verify Agreement response contains required field
      if (
        agreements
        && Array.isArray(agreements.verticalAgreements)
        && agreements.verticalAgreements.length > 0
      ) {
        const [agreement] = agreements.verticalAgreements;
        this.agreement = agreement;
        this.hasAgreement = true;
        this.templateKey = this.normalizeTemplateKey(agreements.templateKey);
      } else {
        this.hasAgreement = false;
        this.agreement = null;
        this.templateKey = null;
      }
      return {
        agreement: this.agreement,
        hasAgreement: this.hasAgreement,
        templateKey: this.templateKey,
      };
    },
    async requestCompanyUuid() {
      const companyUuid = await this.clients.accountClient.getCompanyUuidFromFolderId(
        this.$route.params.folderId,
      );
      this.companyUuid = companyUuid;
      return companyUuid;
    },
    async requestCompany(companyUuid) {
      const company = await this.clients.accountClient.getCompanyByUuid(companyUuid);
      this.company = company;
      this.companyId = company.id;

      return company;
    },
    async requestHasPhoneNumber() {
      this.hasPhoneNumber = await this.clients.accountClient.hasPhoneNumber();
      return this.hasPhoneNumber;
    },
    async updatePhoneNumber(phoneType, phoneNumber) {
      let response;
      try {
        response = await this.clients.accountClient.updatePhone(phoneType, phoneNumber);
      } catch (error) {
        // Swallow error so modal will close
      }

      this.modalCurrentUiState = ModalStates.Complete;
      return response;
    },
    async requestUserIp() {
      const userIp = await this.clients.ipClient.getIp();
      this.userIp = userIp;
      return this.userIp;
    },
    async handleSubmitAgreement() {
      const certificateUrl = this.trustedForm.getTrustedFormCertificateUrl();
      const selections = {
        [this.agreementId]: this.selectedVerticalIds,
      };
      const { postalCode, state } = this.$store.state.Inspection.inspections[0];
      const updateAgreementsPayload = {
        certificateUrl,
        selections,
        location: 'inspection',
        companyId: this.company.id,
        inspectionId: this.$route.params.folderId,
        templateKey: this.templateKey,
        userIp: this.userIp,
        postalCode,
        state,
      };
      let response;
      try {
        response = await this.clients.offersClient.updateAgreements(updateAgreementsPayload);
      } catch (error) {
        // Swallow error so modal will close
      }

      return {
        response,
        updateAgreementsPayload,
      };
    },
    maybeInquirePhoneNumber() {
      if (!this.hasPhoneNumber) {
        this.modalCurrentUiState = ModalStates.Inquiry;
      } else {
        this.modalCurrentUiState = ModalStates.Complete;
      }
    },
    normalizeTemplateKey(maybeTemplateKey) {
      const activeTemplates = Object.keys(OffersModalTemplates);
      return maybeTemplateKey && activeTemplates.includes(maybeTemplateKey)
        ? maybeTemplateKey
        : defaultTemplateKey;
    },
    aggregateSelectedVerticalIds(verticalIds) {
      return Object.entries(verticalIds)
        .filter(([, value]) => !!value)
        .reduce((ids, [key]) => ids.concat(key.includes(',') ? key.split(',') : key), [])
        .map(Number);
    },
    clearSelectedVerticalIds() {
      this.verticalIds = {};
      return this.selectedVerticalIds;
    },
  },
  mounted() {
    this.trustedForm = new TrustedForm();
    this.initializeClients();
    this.initializeAllApis();
    this.$router.onReady(() => {
      this.initializeAllApis().then(isReady => {
        if (isReady) {
          this.initializeVerticals.bind(this);
        }
      });
    });
  },
  watch: {
    $route() {
      this.$nextTick(this.initializeAllApis);
    },
    showModal() {
      document.querySelector('html').classList.toggle('modal-is-active');
    },
  },
};
</script>

<style lang='scss'>
// shared amongst all modals
.offer {
  align-items: center;
  color: #404040;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  $container: &;
  padding: 20px 30px 0 20px;

  .subheader {
    font-family: 'Barlow', sans-serif;
    font-weight: 900;
    color: #ff760a;
    font-size: 26px;
    letter-spacing: 0px;
    text-align: center;
    margin-bottom: 6px;
    text-transform: none;
  }

  .leader {
    align-items: center;
    font-family: 'Barlow Semi Condensed', sans-serif;
    color: #333;
    font-weight: 400;
    font-size: 17px;
    text-align: center;
    line-height: 140%;
    margin-bottom: 1rem;
  }

  .inputs {
    margin-bottom: 1.5rem;
  }

  .disclaimers {
    background-color: #4d4d4d;
    padding: 12px;

    .legal {
      background-color: #626262;
      color: #fff;
      line-height: 13px;
      font-family: 'Barlow Semi Condensed', sans-serif;
      font-weight: 300;
      font-size: 12px;
      height: 60px;
      overflow-y: auto;
      padding: 5px;

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #d2d2d2;
      }

      &::-webkit-scrollbar-thumb {
        background: #888;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      a {
        color: #29abe2;
        text-decoration: underline;
      }
    }

    .policies {
      text-align: center;

      a {
        text-decoration: underline;
      }
    }
  }

  .controls {
    font-family: 'Barlow Semi Condensed', sans-serif;
    font-weight: 600;
  }

  .inputs {
    text-align: left;
  }

  .ornament {
    color: #ff760a;
    transform: rotate(90deg);
  }
}
</style>

<style lang='scss'>
// vuetify overrides

.offer {
  label {
    margin-bottom: inherit;
  }
}

.v-input--checkbox .v-input__slot {
  align-items: flex-start;
}

.v-input--checkbox .v-input__prepend-outer {
  margin-right: 2px;
}
</style>

<style>
html.modal-is-active {
  overflow: hidden;
}
</style>
